// src/components/StartingSoonRail.js
// ============================================================================
// "STARTING SOON" — the discovery path for the pre-event ceremony.
//
// Founder 2026-08-07, watching a full 16-player ceremony run while Live Matches
// showed "0 Live Now": "add a section here in the live matches that includes
// the starting soon — an area where users can click onto the link, and stage
// themselves and watch the ceremony and watch while they wait."
//
// He was right that nothing surfaced it. LiveMatchesScreen queries tournaments
// where status == 'live', but an event in its ceremony window is still
// 'scheduled' / 'locked' / 'seeded' — so the entire pre-race sequence, the part
// built specifically to HOLD an audience before the race, was reachable only by
// someone who already had the /watch/:eventId link.
//
// WINDOW: deliberately reuses the ceremony's own definition rather than
// inventing a threshold. computeCeremonyPhase shows COUNTDOWN once the event is
// locked/seeded OR startAt is within COUNTDOWN_LEAD_MS — so those are exactly
// the events whose watch page has something on it. Every card here leads to a
// screen that is actually staged.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  View, Text, ScrollView, Pressable, StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { collection, doc, onSnapshot, query, where, limit } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc as fsDoc, getDoc } from 'firebase/firestore';

import { db } from '../../firebaseConfig';
import { COUNTDOWN_LEAD_MS, formatCountdown, CEREMONY_COLORS } from '../constants/ceremony';
import { GAME_PROFILES } from '../constants/gameData';
import { useGameAvailability } from '../hooks/useGameAvailability';
import useCritsEnabled from '../hooks/useCritsEnabled';

// An event that is locked but running late still belongs here — it is about to
// go, and its watch page is staged. Past this, treat it as stuck rather than
// "starting soon" so a wedged event can't squat the rail forever.
const LATE_GRACE_MS = 30 * 60 * 1000;

const CEREMONY_STATUSES = ['scheduled', 'locked', 'seeded'];

const ms = (v) => {
  if (!v) return null;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v === 'number') return v;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
};

export default function StartingSoonRail({ navigation }) {
  const [rows, setRows] = useState([]);
  const [ceremonyEnabled, setCeremonyEnabled] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  // Ticks so the countdowns move without refetching anything.
  const [, setTick] = useState(0);

  // Per-game visibility (testing = admins only, hidden = nobody)
  const { isEventVisible } = useGameAvailability();
  const critsEnabled = useCritsEnabled();

  // --- the ship-dark flag (settings/* is world-readable; admins are exempt,
  // mirroring useEventCeremony so this can be exercised before it goes public).
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'settings', 'ceremony'),
      (snap) => setCeremonyEnabled(snap.exists() && snap.data()?.enabled === true),
      () => setCeremonyEnabled(false),
    );
    return unsub;
  }, []);

  useEffect(() => {
    let active = true;
    const unsub = onAuthStateChanged(getAuth(), (u) => {
      if (!u) { if (active) setIsAdminUser(false); return; }
      getDoc(fsDoc(db, 'users', u.uid))
        .then((snap) => {
          if (active) setIsAdminUser(!!(snap.exists() && snap.data()?.role === 'admin'));
        })
        .catch(() => { if (active) setIsAdminUser(false); });
    });
    return () => { active = false; unsub(); };
  }, []);

  // --- candidates. Filter by status only (single-field, no composite index)
  // and apply the time window in code — the set of non-live events is small and
  // this keeps the query from needing an index deploy to ship.
  useEffect(() => {
    const q = query(
      collection(db, 'tournaments'),
      where('status', 'in', CEREMONY_STATUSES),
      limit(50),
    );
    const unsub = onSnapshot(
      q,
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => { console.warn('[StartingSoonRail]', err.message); setRows([]); },
    );
    return unsub;
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const visible = ceremonyEnabled || isAdminUser;

  const staged = useMemo(() => {
    if (!visible) return [];
    const now = Date.now();
    return rows
      .map((ev) => ({ ev, startsAtMs: ms(ev.startAt) }))
      .filter(({ ev, startsAtMs }) => {
        if (ev.visibility === 'private') return false;
        if (!isEventVisible(ev)) return false;
        if (startsAtMs == null) return false;
        const until = startsAtMs - now;
        if (until < -LATE_GRACE_MS) return false;
        // Mirrors computeCeremonyPhase's COUNTDOWN gate.
        const locked = !!ev.lockedAt || ev.status === 'locked' || ev.status === 'seeded';
        return locked || until <= COUNTDOWN_LEAD_MS;
      })
      .sort((a, b) => a.startsAtMs - b.startsAtMs);
  }, [rows, visible, isEventVisible]);

  if (!staged.length) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.header}>
        <View style={styles.headerRow}>
          <View style={styles.pulse} />
          <Text style={styles.title}>Starting Soon</Text>
        </View>
        <Text style={styles.subtitle}>
          Not live yet — jump in early and watch the grid fill up.
        </Text>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.rail}
      >
        {staged.map(({ ev, startsAtMs }) => (
          <StartingSoonCard
            key={ev.id}
            ev={ev}
            startsAtMs={startsAtMs}
            critsEnabled={critsEnabled}
            onPress={() => navigation.navigate('EventWatch', { eventId: ev.id })}
          />
        ))}
      </ScrollView>
    </View>
  );
}

function StartingSoonCard({ ev, startsAtMs, onPress, critsEnabled = true }) {
  const until = startsAtMs - Date.now();
  const gp = GAME_PROFILES[String(ev.game || '').toLowerCase()];
  const gameLabel = gp?.name || String(ev.game || '').toUpperCase();

  const ready = Number(ev.playersReady) || 0;
  const required = Number(ev.totalPlayersRequired) || 0;
  const entrants = Number(ev.entrantsCount) || 0;
  const capacity = Number(ev.maxEntrants) || 0;

  // Crits only. Cash never appears on a public surface.
  const critPool = ev.entryCurrency === 'crits' ? Number(ev.critPotCount) || 0 : 0;

  return (
    <Pressable style={styles.card} onPress={onPress}>
      <View style={styles.cardTop}>
        <Text style={styles.gameTag} numberOfLines={1}>{gameLabel}</Text>
        <View style={styles.countdownPill}>
          <Ionicons name="time-outline" size={11} color="#0b0d12" />
          <Text style={styles.countdownText}>
            {until > 0 ? formatCountdown(until) : 'ANY MOMENT'}
          </Text>
        </View>
      </View>

      <Text style={styles.cardTitle} numberOfLines={2}>{ev.name || 'Event'}</Text>

      <View style={styles.metaRow}>
        {required > 0 ? (
          <Text style={styles.meta}>
            <Text style={styles.metaStrong}>{ready}</Text>
            <Text style={styles.metaDim}>/{required} </Text>
            players ready
          </Text>
        ) : (
          <Text style={styles.meta}>
            <Text style={styles.metaStrong}>{entrants}</Text>
            {capacity ? <Text style={styles.metaDim}>/{capacity} </Text> : ' '}
            entered
          </Text>
        )}
      </View>

      {critPool > 0 && (
        <Text style={styles.pool}>{critsEnabled ? `${critPool.toLocaleString()} Crit pool` : 'Free entry'}</Text>
      )}

      <View style={styles.watchRow}>
        <Ionicons name="play-circle-outline" size={14} color={CEREMONY_COLORS.GOLD} />
        <Text style={styles.watchText}>Watch the build-up</Text>
      </View>
    </Pressable>
  );
}

const GOLD = '#FCD000';

const styles = StyleSheet.create({
  wrap: { marginTop: 8, marginBottom: 4 },
  header: { paddingHorizontal: 16, marginBottom: 10 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  pulse: {
    width: 8, height: 8, borderRadius: 4, backgroundColor: GOLD,
  },
  title: { color: '#fff', fontSize: 18, fontWeight: '900', letterSpacing: 0.3 },
  subtitle: { color: '#8b93a7', fontSize: 12, marginTop: 3 },
  rail: { paddingHorizontal: 16, gap: 12, paddingBottom: 4 },
  card: {
    width: 240,
    backgroundColor: '#12151c',
    borderWidth: 1,
    borderColor: '#2a2f3d',
    borderRadius: 12,
    padding: 12,
    justifyContent: 'space-between',
  },
  cardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  gameTag: {
    color: '#7FD4FF', fontSize: 10, fontWeight: '800', letterSpacing: 1, flexShrink: 1,
  },
  countdownPill: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    backgroundColor: GOLD, borderRadius: 999, paddingHorizontal: 7, paddingVertical: 2,
  },
  countdownText: { color: '#0b0d12', fontSize: 10, fontWeight: '900', letterSpacing: 0.5 },
  cardTitle: { color: '#fff', fontSize: 14, fontWeight: '800', marginTop: 10, lineHeight: 18 },
  metaRow: { marginTop: 8 },
  meta: { color: '#aeb6c8', fontSize: 11 },
  metaStrong: { color: '#fff', fontWeight: '900' },
  metaDim: { color: '#6c7488' },
  pool: { color: GOLD, fontSize: 11, fontWeight: '800', marginTop: 4 },
  watchRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 10 },
  watchText: { color: GOLD, fontSize: 11, fontWeight: '700' },
});
